import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import useLessons from '@/hooks/api/useLessons';
import ProgramSelect from './ProgramSelect';
import ReuseProgramContent from './ReuseProgramContent';
import useProgramContentStore from './useProgramContentStore';
import useUnits from './useUnits';

interface AddLessonProps {
  parentProgramId: number;
  modalManager: ModalManager
}

const AddLessonsModal = ({ parentProgramId, modalManager }: AddLessonProps) => {
  const { isOpen, close } = modalManager;
  const [selectedOption, setSelectedOption] = useState('reuse');
  const [programId, setProgramId] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const { lessons } = useLessons({ attributes: ['id'], programId: parentProgramId });
  const { units } = useUnits({ attributes: ['id'], programId: parentProgramId });

  const copyLessonIds = useProgramContentStore(state => state.copyLessonIds);
  const copyUnitIds = useProgramContentStore(state => state.copyUnitIds);
  const reuseLessonIds = useProgramContentStore(state => state.reuseLessonIds);
  const reuseUnitIds = useProgramContentStore(state => state.reuseUnitIds);
  const isAnyChecked = useProgramContentStore(state => state.isAnyChecked);
  const reset = useProgramContentStore(state => state.reset);
  const setParentLessonIds = useProgramContentStore(state => state.setParentLessonIds);
  const setParentUnitIds = useProgramContentStore(state => state.setParentUnitIds);

  useEffect(() => {
    setParentLessonIds(lessons.map(l => l.id));
  }, [lessons]);

  useEffect(() => {
    setParentUnitIds(units.map(l => l.id));
  }, [units]);

  const onSubmit = () => {
    setIsLoading(true);
    if (selectedOption === 'create') {
      window.location.assign(Routes.new_admin_program_lesson_path(parentProgramId));
      return;
    }
    Axios
      .post(Routes.plato_api_copy_resources_program_path(parentProgramId), {
        copy_lesson_ids: copyLessonIds,
        copy_unit_ids: copyUnitIds,
        reuse_lesson_ids: reuseLessonIds,
        reuse_unit_ids: reuseUnitIds,
        source_program_id: programId,
      })
      .then(() => window.location.reload())
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    setProgramId(null);
  };

  return (
    <SubmitModal
      nearlyFullScreen
      bodyClassName="tw-flex tw-flex-col tw-justify-between"
      close={close}
      headerText="Add Lesson"
      isLoading={isLoading}
      isOpen={isOpen}
      isValid={selectedOption === 'create' || isAnyChecked()}
      submitText="Add Content"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="tw-h-full tw-overflow-scroll">
        <p>Create a lesson from scratch or reuse an existing one from another program.</p>

        <div className="tw-flex tw-flex-col">
          <label className="tw-flex tw-items-center tw-mb-2">
            <input
              checked={selectedOption === 'create'}
              type="radio"
              value="create"
              onChange={handleOptionChange}
            />
            <span>Create new lesson</span>
          </label>
          <label className="tw-flex tw-items-center tw-mb-2">
            <input
              checked={selectedOption === 'reuse'}
              type="radio"
              value="reuse"
              onChange={handleOptionChange}
            />
            <span>Reuse a lesson from an existing program</span>
          </label>
          {selectedOption === 'reuse' && (
            <>
              <ProgramSelect
                onSelect={(value) => {
                  setProgramId(value);
                  reset();
                }}
              />
              {programId && (
                <ReuseProgramContent
                  programId={programId}
                />
              )}
            </>
          )}
        </div>
      </div>
    </SubmitModal>
  );
};

export default AddLessonsModal;
